import { Component, EventEmitter, Output, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { catchError, finalize, forkJoin, of, Subject, Subscription, takeUntil } from 'rxjs';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  extractObject,
  generalConfigFromGlobal,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';
import { ReclassLocalChartService } from './reclass-local-chart.service';

interface AccountModel {
  ACCTTP: string;
  LOCAL_ACCT_KEY: number;
  SYSTEM_ACCT_KEY: number;
  SYSTEM_ACCT: string;
  SYSTEM_ACCT_DESC: string;
  LOCAL_ACCT: string;
  LOCAL_ACCT_DESC: string;
  TP_ENTITY: string;
  TP_KEY: number;
  TP_REP_PD: number;
  IS_TP_EXISTS: boolean;
  BOOK_AMT: number | null;
  PREV_ADJ_AMT: number | null;
  CUR_ADJ_AMT: number | null;
  TOTAL?: number;
  TYPE: string;
  IS_DELETE: string;
  TRANS_DETAILS_KEY: number;
  ISBOOKAMTLOADING: boolean;
  ISBOOKADJLOADING: boolean;
  INVALIDACCTTP: boolean;
  tpObject?: any;
  tpSearchText?: string;
  selected?: any;
}

@Component({
    selector: 'gtw-reclass-local-chart',
    templateUrl: './reclass-local-chart.component.html',
    styleUrls: ['./reclass-local-chart.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ReclassLocalChartComponent implements OnInit, OnDestroy {

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private isInitialized = false;
  private isLoading = false;
  /** Bumped on `reset()` and at each batched load so late HTTP responses do not mutate UI. */
  private dataLoadSeq = 0;
  private unsubscribe$ = new Subject<void>();
  private readonly contextSubs = new Subscription();

  title = 'Book Adjustment';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;
  existingLoading = false;
  noRecords = false;
  viewOnly = false;
  showReclassForm = true;
  showReclassType = true;
  showNovYrReclassType = true;
  loadingSysAcct = true;
  loadingTpAcct = true;
  isMaxLimitReached = false;

  RECLASS_DESCRIPTION = '';
  RECLASS_TYPE = 'RCY';
  TRANS_HEADER_KEY: string | number = '';
  reclassTotal: number = 0;
  accounts: AccountModel[] = [];
  reclassArray: any[] = [];
  delTransDetails: any[] = [];

  localAccDropdownData: any[] = [];
  limitedAccDropdownData: any[] = [];
  tpDropdownData: any[] = [];
  limitedTpDropdownData: any[] = [];
  tpSearchResults: any[] = [];
  loadingTpSearch = false;
  activeTPSearchIndex = -1;
  reclassDescDropdownData: any[] = [];
  commentsDescDropdownData: any[] = [];
  reclassDescFullData: any[] = [];
  commentsDescFullData: any[] = [];
  lazyLoadObj: any = {};

  private static readonly DROPDOWN_LIMIT = 150;

  modal = { R_STD_DESC: '', R_CMT: '' };
  codeCombination: any = null;

  taxYearBegin = '';
  taxYearEnd = '';
  mainScreenKey: number = 0;
  gridType = '';
  sourceAcctType: number | null = null;

  baseURLs: GtwElementsBaseUrlsModel = { admin: '', api: '', custom: '', efile: '', pdfEngine: '', bulkPdfEngine: '' };
  globalService: any;
  GENERAL_CONFIG: any;
  rowData: any = {};
  filterParams: any = {};

  isCommentsMaxLimitReached = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private _snackBar: MatSnackBar,
    private reclassService: ReclassLocalChartService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.contextSubs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.applyWorkspaceContext();
        this.reset();
      })
    );
    this.contextSubs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.applyWorkspaceContext();
        this.reset();
      })
    );
  }

  ngOnDestroy(): void {
    this.contextSubs.unsubscribe();
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const shell = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    const hydrated = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen);
    if (!Object.keys(fp).length) {
      fp = extractObject(hydrated);
    }
    this.filterParams = fp;
    const mergedGp = { ...extractObject(shell.globalParams), ...extractObject(hydrated) };
    this.globalService = {
      ...shell,
      globalParams: mergedGp,
      filterParams: fp,
      workspaceFactory: this.gtwWorkspace.workspaceFactory,
      getScenario: (ty: any) => this.gtwGlobal.getScenario(ty),
      getEIN: () => this.gtwGlobal.getEIN(),
      getTAXYEAR: () => this.gtwGlobal.getTAXYEAR(),
      getChartType: () => this.gtwGlobal.getChartType(),
      inputs: this.gtwGlobal.inputs,
      sso_id: this.gtwApp?.USER_SETTINGS?.user?.sso_id ?? ''
    };
    const gc = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = { ...gc, base_url: gc.base_url || gc.api || '' };
    this.baseURLs = gc;
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    this.rowData = Array.isArray(rawRow)
      ? extractObject(rawRow[0]?.data ?? rawRow[0])
      : extractObject(rawRow);
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized || this.isLoading) return;
    if (!this.globalService || !this.GENERAL_CONFIG || !this.rowData || Object.keys(this.rowData).length === 0) return;
    this.isLoading = true;
    this.isInitialized = true;
    this.init();
  }

  init(): void {
    try {
      this.setupInitialData();
      this.loadAllData();
    } catch (e) {
      console.error('Error in component init:', e);
      this.showFormLoader = false;
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private setupInitialData(): void {
    this.gridType = this.rowData.GRID_TYPE || '';
    this.sourceAcctType = this.rowData.SYSTEM_ACCT_TYPE;
    const screenKeyRaw = this.globalService?.globalParams?.screen_key;
    this.mainScreenKey = screenKeyRaw ? parseInt(String(screenKeyRaw), 10) : 0;

    if (this.rowData.TAX_YEAR_BEGIN) {
      const d = new Date(this.rowData.TAX_YEAR_BEGIN);
      this.taxYearBegin = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
    }
    if (this.rowData.TAX_YEAR_END) {
      const d = new Date(this.rowData.TAX_YEAR_END);
      this.taxYearEnd = `${String(d.getMonth() + 1).padStart(2, '0')}/${String(d.getDate()).padStart(2, '0')}/${d.getFullYear()}`;
    }

    this.showReclassType = this.rowData.SOURCE_SYSTEM !== 'DCS';
    this.showNovYrReclassType = this.rowData.NOV_YEAR_END !== 'N';

    if (this.rowData.TRANS_TYPE_ACTUAL) {
      switch (this.rowData.TRANS_TYPE_ACTUAL) {
        case 'LOCAL_BOOK_ADJ': this.RECLASS_TYPE = 'RCY'; break;
        case 'LOCAL_BOOK_ADJ_CY': this.RECLASS_TYPE = 'CYD'; break;
        case 'LOCAL_BOOK_ADJ_PY': this.RECLASS_TYPE = 'PYD'; break;
        default: this.RECLASS_TYPE = 'RCY';
      }
    }

    if (this.gridType === 'LOCAL_BOOK_TO_TAX_SUMMARY') {
      this.buildAccountRecord(this.rowData, 0);
    } else {
      this.accounts.push(this.createBlankAccount());
    }
  }

  /**
   * Single batched load: fires all API calls in parallel, single detectChanges at the end.
   */
  private loadAllData(): void {
    const seq = ++this.dataLoadSeq;
    const balSheetAccts = [1, 2, 3];
    const acctType = this.sourceAcctType || 0;
    const acctTypeStr = balSheetAccts.includes(acctType) ? '1,2,3' : '5,6,7';
    const isSystemAcctSearch = this.gridType === 'SYSTEM_BOOK_TO_TAX_SUMMARY' ? 'Y' : 'N';
    const taxYear = this.filterParams.tax_year || this.globalService?.globalParams?.tax_year || this.rowData.TAX_YEAR;
    const scenario = this.filterParams.scenario || this.globalService?.globalParams?.scenario || this.rowData.SCENARIO;
    const isDCS = this.rowData.SOURCE_SYSTEM === 'DCS';

    const accounts$ = this.reclassService.getAccounts(
      this.GENERAL_CONFIG.base_url,
      {
        screen_key: this.mainScreenKey,
        combination_key: this.rowData.COMBINATION_KEY,
        ge_gecs_entity: this.rowData.GE_GECS_ENTITY || '',
        chart_key: this.rowData.LOCAL_CHART_KEY,
        parent_system_acct_key: this.rowData.SYSTEM_ACCT_KEY || 0,
        source_type: isDCS ? 'D' : 'F',
        search_type: 'ACCT',
        search_string: '',
        acct_type_str: acctTypeStr,
        system_acct_search: isSystemAcctSearch
      },
      this.globalService
    ).pipe(catchError(() => of([])));

    // Legacy uses getTradingPartnersFir (50swfi) for both DCS and FIR.
    // The DCS-specific path (sckfcq) was deprecated in legacy.
    const tp$ = this.reclassService.getTradingPartnersFir(
      this.GENERAL_CONFIG.base_url, taxYear, scenario, '', this.globalService
    ).pipe(catchError(() => of([])));

    const existing$ = this.reclassService.loadExistingReclass(
      this.GENERAL_CONFIG.base_url,
      this.mainScreenKey,
      this.rowData.COMBINATION_KEY,
      this.rowData.LOCAL_CHART_KEY,
      this.rowData.SYSTEM_ACCT_KEY || 0,
      this.globalService
    ).pipe(catchError(() => of([])));

    const jcdKey = this.globalService?.globalParams?.jcd_key || this.filterParams.jcd_key;
    const stdDesc$ = isDCS
      ? this.reclassService.getStdReclassDescriptions(this.GENERAL_CONFIG.base_url, taxYear, scenario, jcdKey, this.globalService).pipe(catchError(() => of([])))
      : of([]);

    const stdCmt$ = isDCS
      ? this.reclassService.getStdReclassComments(this.GENERAL_CONFIG.base_url, taxYear, scenario, jcdKey, this.globalService).pipe(catchError(() => of([])))
      : of([]);

    // When routed from dispatcher (TRANS_TYPE_ACTUAL set), fetch Book Amt & Other Adjs via separate API
    const hasTransType = this.rowData.hasOwnProperty('TRANS_TYPE_ACTUAL');
    let dtlsAdj$: any = of([]);
    if (hasTransType) {
      let adjSource = 'M';
      switch (this.rowData.TRANS_TYPE_ACTUAL) {
        case 'LOCAL_BOOK_ADJ': adjSource = 'M'; break;
        case 'LOCAL_BOOK_ADJ_PY': adjSource = 'P'; break;
        default: adjSource = 'C'; break;
      }
      dtlsAdj$ = this.reclassService.getAmountsForDtlsAdj(
        this.GENERAL_CONFIG.base_url,
        this.rowData.COMBINATION_KEY,
        this.rowData.LOCAL_ACCT_KEY,
        this.rowData.LOCAL_CHART_KEY,
        this.rowData.TP_KEY || 0,
        adjSource,
        this.globalService
      ).pipe(catchError(() => of([])));
    }

    const codeCombo$ = (!isDCS && this.rowData.COMBINATION_KEY)
      ? this.reclassService.getCodeCombination(this.GENERAL_CONFIG.base_url, this.rowData.COMBINATION_KEY).pipe(catchError(() => of(null)))
      : of(null);

    forkJoin([accounts$, tp$, existing$, stdDesc$, stdCmt$, dtlsAdj$, codeCombo$])
      .pipe(
        takeUntil(this.unsubscribe$),
        finalize(() => {
          if (seq === this.dataLoadSeq) {
            this.isLoading = false;
            this.cdr.detectChanges();
          }
        })
      )
      .subscribe(([accountsData, tpData, existingData, stdDescData, stdCmtData, dtlsAdjData, codeComboData]: any[]) => {
        if (seq !== this.dataLoadSeq) {
          return;
        }
        if (codeComboData) {
          this.codeCombination = codeComboData;
        }
        this.localAccDropdownData = accountsData || [];
        this.limitedAccDropdownData = this.localAccDropdownData;
        this.lazyLoadObj.totalDrpList = this.localAccDropdownData;
        this.lazyLoadObj.cachedDrpList = this.localAccDropdownData.slice(0, 10);
        this.loadingSysAcct = false;

        this.tpDropdownData = tpData || [];
        this.limitedTpDropdownData = this.tpDropdownData;
        this.loadingTpAcct = false;

        this.reclassArray = this.formatReclassData(existingData || []);
        this.setExistingReclassTotal(this.reclassArray);
        this.existingLoading = false;
        this.noRecords = this.reclassArray.length === 0;

        if (stdDescData && stdDescData.length) {
          this.reclassDescFullData = stdDescData;
          this.reclassDescDropdownData = [...new Set(stdDescData.map((item: any) => item.RECLASSSTDDESC))];
        }
        if (stdCmtData && stdCmtData.length) {
          this.commentsDescFullData = stdCmtData;
          this.commentsDescDropdownData = [...new Set(stdCmtData.map((item: any) => item.RECLASSSTDCOMMENTS))];
        }

        if (dtlsAdjData && dtlsAdjData.length && this.accounts.length > 0) {
          const sourceAcct = this.accounts.find(a => a.TYPE === 'source');
          if (sourceAcct) {
            sourceAcct.BOOK_AMT = dtlsAdjData[0].LOCAL_BOOK_AMT;
            sourceAcct.PREV_ADJ_AMT = dtlsAdjData[0].LOCAL_BOOK_ADJ;
          }
        }

        this.showFormLoader = false;
        this.cdr.detectChanges();
      });
  }

  private createBlankAccount(): AccountModel {
    return {
      ACCTTP: '', LOCAL_ACCT_KEY: 0, SYSTEM_ACCT_KEY: 0,
      SYSTEM_ACCT: '', SYSTEM_ACCT_DESC: '', LOCAL_ACCT: '', LOCAL_ACCT_DESC: '',
      TP_ENTITY: '', TP_KEY: 0, TP_REP_PD: 0, IS_TP_EXISTS: false,
      BOOK_AMT: null, PREV_ADJ_AMT: null, CUR_ADJ_AMT: null,
      TYPE: 'blank', IS_DELETE: 'NO', TRANS_DETAILS_KEY: 0,
      ISBOOKAMTLOADING: false, ISBOOKADJLOADING: false, INVALIDACCTTP: false
    };
  }

  addTargetAccount(): void {
    this.accounts.push(this.createBlankAccount());
    this.cdr.detectChanges();
  }

  removeAccount(index: number): void {
    const account = this.accounts[index];
    if (account.TYPE === 'edit') {
      account.IS_DELETE = 'YES';
      this.delTransDetails.push(account);
    }
    this.accounts.splice(index, 1);
    this.recalcReclassTotal();
    this.cdr.detectChanges();
  }

  private formatReclassData(data: any[]): any[] {
    const reclasses: any[] = [];
    const accounts: any[] = [];
    data.forEach(item => {
      if (item.ROW_INDICATOR === 1) { reclasses.push({ ...item, ACCOUNTS: [] }); }
      else { accounts.push({ ...item, TYPE: 'edit' }); }
    });
    reclasses.forEach(r => {
      r.ACCOUNTS = accounts.filter(a => a.TRANS_HEADER_KEY === r.TRANS_HEADER_KEY);
    });
    return reclasses;
  }

  private setExistingReclassTotal(array: any[]): void {
    array.forEach(r => {
      if (r.ACCOUNTS) {
        r.ACCOUNTS.forEach((a: any) => {
          a.TOTAL = (parseFloat(a.BOOK_AMT) || 0) + (parseFloat(a.PREV_ADJ_AMT) || 0) + (parseFloat(a.CUR_ADJ_AMT) || 0);
        });
      }
    });
  }

  private buildAccountRecord(data: any, index: number): void {
    const tpExists = data.TP_KEY !== 0;
    const tpRepPd = tpExists ? data.TP_REP_PD : 0;
    const tpEntity = tpExists ? `${data.TP_LEID}-${data.TP_CDR_NO}(Pd:${tpRepPd})` : '';

    let totBookAdj = data.BOOK_ADJ || 0;
    if (index < 1 && this.gridType === 'LOCAL_BOOK_TO_TAX_SUMMARY') {
      totBookAdj = (data.BOOK_ADJ || 0) + (data.PY_DECEMBER || 0) + (data.CY_DECEMBER || 0);
    }

    const record: AccountModel = {
      ACCTTP: `${data.LOCAL_ACCT_KEY}${data.TP_KEY}${tpRepPd}`,
      LOCAL_ACCT_KEY: data.LOCAL_ACCT_KEY, SYSTEM_ACCT_KEY: data.SYSTEM_ACCT_KEY,
      SYSTEM_ACCT: data.SYSTEM_ACCT, SYSTEM_ACCT_DESC: data.SYSTEM_ACCT_DESC,
      LOCAL_ACCT: data.LOCAL_ACCT, LOCAL_ACCT_DESC: data.LOCAL_ACCT_DESC,
      TP_ENTITY: tpEntity, TP_KEY: data.TP_KEY, TP_REP_PD: tpRepPd, IS_TP_EXISTS: tpExists,
      BOOK_AMT: data.BOOK_AMT, PREV_ADJ_AMT: totBookAdj, CUR_ADJ_AMT: 0,
      TYPE: index < 1 ? 'source' : 'target',
      TRANS_DETAILS_KEY: 0, IS_DELETE: 'NO',
      ISBOOKAMTLOADING: false, ISBOOKADJLOADING: false, INVALIDACCTTP: false
    };

    if (data.TP_KEY !== 0) {
      record.tpObject = { TP_ENTITY: tpEntity, TP_KEY: data.TP_KEY, TP_REP_PD: tpRepPd, TP_LEID: data.TP_LEID, TP_CDR_NO: data.TP_CDR_NO };
      record.tpSearchText = tpEntity;
    }

    if (this.accounts[index] === undefined) {
      record.TYPE = 'source';
      this.accounts.push(record);
      this.accounts.push(this.createBlankAccount());
    } else {
      record.TYPE = 'target';
      if (index === 0) {
        if (this.gridType === 'LOCAL_BOOK_TO_TAX_SUMMARY') record.TYPE = 'source';
        this.accounts.push(this.createBlankAccount());
      }
      this.accounts[index] = record;
    }
  }

  setAccount(account: any, index: number): void {
    if (!account) return;
    account.COMBINATION_KEY = this.rowData.COMBINATION_KEY;
    this.buildAccountRecord(account, index);
    this.cdr.detectChanges();
  }

  setTradingPartner(tp: any, index: number): void {
    if (!tp) return;
    const currAcct = this.accounts[index];
    currAcct.TP_ENTITY = `${tp.TP_LEID}-${tp.TP_CDR_NO}(Pd:${tp.TP_REP_PD})`;
    currAcct.TP_KEY = tp.TP_KEY;
    currAcct.TP_REP_PD = tp.TP_REP_PD;
    currAcct.ACCTTP = `${currAcct.LOCAL_ACCT_KEY}${tp.TP_KEY}${tp.TP_REP_PD}`;

    if (tp.TP_KEY !== 0) {
      currAcct.ISBOOKAMTLOADING = true;
      currAcct.ISBOOKADJLOADING = true;
      this.cdr.detectChanges();

      this.reclassService.getBookAmtByTp(
        this.GENERAL_CONFIG.base_url, this.mainScreenKey,
        this.rowData.COMBINATION_KEY, this.rowData.LOCAL_CHART_KEY,
        this.rowData.SOURCE_SYSTEM === 'DCS' ? 'D' : 'F', 'TP',
        `${currAcct.LOCAL_ACCT_KEY}${currAcct.TP_KEY}`, this.globalService
      ).pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any[]) => {
          currAcct.ISBOOKAMTLOADING = false;
          currAcct.ISBOOKADJLOADING = false;
          if (data.length) { currAcct.BOOK_AMT = data[0].BOOK_AMT; currAcct.PREV_ADJ_AMT = data[0].BOOK_ADJ; }
          else { currAcct.BOOK_AMT = 0; currAcct.PREV_ADJ_AMT = 0; }
          this.cdr.detectChanges();
        },
        () => {
          currAcct.ISBOOKAMTLOADING = false;
          currAcct.ISBOOKADJLOADING = false;
          currAcct.BOOK_AMT = 0;
          currAcct.PREV_ADJ_AMT = 0;
          this.cdr.detectChanges();
        }
      );
    }
  }

  searchTradingPartners(event: any, index: number): void {
    const val = (event.target as HTMLInputElement).value;
    this.accounts[index].tpSearchText = val;
    if (!val || val.length < 3) {
      this.tpSearchResults = [];
      this.activeTPSearchIndex = -1;
      this.cdr.detectChanges();
      return;
    }
    this.activeTPSearchIndex = index;
    this.loadingTpSearch = true;
    const taxYear = this.filterParams?.tax_year || this.globalService?.globalParams?.tax_year;
    const scenario = this.filterParams?.scenario || this.globalService?.globalParams?.scenario;
    // Legacy uses getTradingPartners_fir (50swfi) for both DCS and FIR typeahead search.
    // The DCS-specific dropdown (sckfcq) was deprecated/commented out in legacy.
    this.reclassService.getTradingPartnersFir(this.GENERAL_CONFIG.base_url, taxYear, scenario, val, this.globalService)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (data: any[]) => { this.tpSearchResults = data || []; this.loadingTpSearch = false; this.cdr.detectChanges(); },
        () => { this.tpSearchResults = []; this.loadingTpSearch = false; this.cdr.detectChanges(); }
      );
  }

  selectTradingPartner(tp: any, index: number): void {
    this.setTradingPartner(tp, index);
    this.accounts[index].tpSearchText = `${tp.TP_LEID}-${tp.TP_CDR_NO}(Pd:${tp.TP_REP_PD})`;
    this.tpSearchResults = [];
    this.activeTPSearchIndex = -1;
    this.cdr.detectChanges();
  }

  closeTpOverlay(): void {
    if (this.tpSearchResults.length) {
      this.tpSearchResults = [];
      this.activeTPSearchIndex = -1;
      this.cdr.detectChanges();
    }
  }

  setSourceTotal(index: number): void {
    this.recalcAccountTotal(index);
    this.recalcReclassTotal();
    this.cdr.detectChanges();
  }

  private recalcAccountTotal(index: number): void {
    const account = this.accounts[index];
    account.TOTAL = (parseFloat(String(account.BOOK_AMT)) || 0) +
                    (parseFloat(String(account.PREV_ADJ_AMT)) || 0) +
                    (parseFloat(String(account.CUR_ADJ_AMT)) || 0);
  }

  private recalcReclassTotal(): void {
    let total = 0;
    this.accounts.forEach(a => {
      if (a.CUR_ADJ_AMT !== null && a.CUR_ADJ_AMT !== undefined && !isNaN(Number(a.CUR_ADJ_AMT))) {
        total += parseFloat(String(a.CUR_ADJ_AMT));
      }
    });
    this.reclassTotal = total;
  }

  toggleMinimise(): void {
    this.minimise = !this.minimise;
    this.cdr.detectChanges();
  }

  removeMessage(): void { this.isMaxLimitReached = false; }

  showDetails(index: number, show: boolean): void {
    this.reclassArray[index].open = show;
    this.cdr.detectChanges();
  }

  loadReclass(index: number): void {
    this.delTransDetails = [];
    const data = this.reclassArray[index];
    this.RECLASS_DESCRIPTION = data.ADJ_DESC;
    if (this.rowData.SOURCE_SYSTEM === 'DCS') {
      this.modal.R_STD_DESC = data.RECLASS_STD_DESC;
      this.modal.R_CMT = data.RECLASS_STD_COMMENTS;
    }
    this.TRANS_HEADER_KEY = data.TRANS_HEADER_KEY;
    this.accounts = data.ACCOUNTS.map((a: any) => ({
      ...a,
      tpSearchText: a.tpSearchText || a.TP_ENTITY || ''
    }));
    this.RECLASS_TYPE = data.SHORT_ADJ_TYPE;
    this.viewOnly = data.SHORT_ADJ_TYPE === 'A';
    this.accounts.forEach((_, key) => this.recalcAccountTotal(key));
    this.recalcReclassTotal();
    this.cdr.detectChanges();
  }

  deleteReclass(reclass: any): void {
    this.existingLoading = true;
    this.cdr.detectChanges();

    const returnObj = {
      trans_header_key: reclass.TRANS_HEADER_KEY, type: 'D',
      combination_key: this.rowData.COMBINATION_KEY, chart_key: this.rowData.LOCAL_CHART_KEY
    };
    const deleteObj = reclass.ACCOUNTS.map((a: any) => ({
      COMBINATION_KEY: this.rowData.COMBINATION_KEY, ACCT_KEY: a.LOCAL_ACCT_KEY,
      CHART_KEY: this.rowData.LOCAL_CHART_KEY, TP_KEY: a.TP_KEY
    }));

    this.reclassService.deleteReclass(this.GENERAL_CONFIG.base_url, returnObj, deleteObj, this.globalService, this.filterParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response: any) => {
          if (response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
            this.existingLoading = false;
          } else {
            this._snackBar.open('Reclass has been successfully deleted', 'Close', { duration: 4000, panelClass: ['success-snackbar'] });
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        () => {
          this._snackBar.open('Error deleting reclass', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
          this.existingLoading = false;
          this.cdr.detectChanges();
        }
      );
  }

  reset(): void {
    this.dataLoadSeq++;
    this.isInitialized = false;
    this.isLoading = false;
    this.accounts = [];
    this.delTransDetails = [];
    this.RECLASS_DESCRIPTION = '';
    this.modal.R_STD_DESC = '';
    this.modal.R_CMT = '';
    this.TRANS_HEADER_KEY = '';
    this.isMaxLimitReached = false;
    this.isCommentsMaxLimitReached = false;
    this.viewOnly = false;
    this.tpSearchResults = [];
    this.activeTPSearchIndex = -1;
    this.codeCombination = null;
    this.reclassTotal = 0;
    this.tryInit();
    this.recalcReclassTotal();
    this.cdr.detectChanges();
  }

  onR_CmtChange(value: string): void {
    this.modal.R_CMT = value;
  }
  validateComments(): void {
    const len = this.modal?.R_CMT?.length ?? 0;
    this.isCommentsMaxLimitReached = len >= 1000;
  }
  removeCommentsMessage(): void {
    this.isCommentsMaxLimitReached = false;
  }

  save(): void {
    this.isMaxLimitReached = false;
    if (!this.RECLASS_DESCRIPTION || this.RECLASS_DESCRIPTION.trim() === '') {
      this._snackBar.open('Book Adjustment Description is required', 'Close', { duration: 4000, panelClass: ['warning-snackbar'] });
      return;
    }
    if (this.reclassTotal !== 0) {
      this._snackBar.open('Reclass must net to 0', 'Close', { duration: 4000, panelClass: ['warning-snackbar'] });
      return;
    }
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', { duration: 4000, panelClass: ['info-snackbar'] });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    let reclassMainType: string, reclassSubtype: string;
    switch (this.RECLASS_TYPE) {
      case 'RCY': reclassMainType = 'R'; reclassSubtype = 'M'; break;
      case 'CYD': reclassMainType = 'R'; reclassSubtype = 'C'; break;
      case 'PYD': reclassMainType = 'R'; reclassSubtype = 'P'; break;
      case 'SCY': reclassMainType = 'S'; reclassSubtype = 'M'; break;
      default: reclassMainType = 'R'; reclassSubtype = 'M';
    }

    const txIssueId = this.globalService?.globalParams?.issue_id_for_tx_save;
    const issueKey = (txIssueId === undefined || txIssueId === '')
      ? (this.globalService?.globalParams?.issue_id ? parseInt(this.globalService.globalParams.issue_id) : null)
      : txIssueId;

    const returnObj: any = {
      RECLASS_DESCRIPTION: this.RECLASS_DESCRIPTION, ISSUE_KEY: issueKey,
      TYPE: this.TRANS_HEADER_KEY !== '' ? 'U' : 'C',
      TRANS_HEADER_KEY: this.TRANS_HEADER_KEY !== '' ? this.TRANS_HEADER_KEY : 0,
      tax_year: this.filterParams.tax_year || this.globalService?.globalParams?.tax_year,
      RECLASSMAINTYPE: reclassMainType, RECLASSSUBTYPE: reclassSubtype,
      ADJ_ORIGIN_SOURCE: this.rowData.SOURCE_SYSTEM,
      SOURCE_SYSTEM_FLAG: this.rowData.SOURCE_SYSTEM === 'DCS' ? 'D' : 'F',
      COMBINATION_KEY: this.rowData.COMBINATION_KEY, CHART_KEY: this.rowData.LOCAL_CHART_KEY,
      SCREEN_KEY: 23, TRANS_DETAILS_KEY: {}
    };

    if (this.rowData.SOURCE_SYSTEM === 'DCS') {
      const descMatch = this.reclassDescFullData.find((i: any) => i.RECLASSSTDDESC === this.modal.R_STD_DESC);
      returnObj.reclassStdDesc = descMatch ? descMatch.RECLASS_STD_DESC_KEY : '';
      const cmtMatch = this.commentsDescFullData.find((i: any) => i.RECLASSSTDCOMMENTS === this.modal.R_CMT);
      returnObj.reclassStdComments = cmtMatch ? cmtMatch.RECLASS_STD_COMMENTS_KEY : '';
    }

    const accountArray = [
      ...this.accounts.map(a => ({ ACCOUNT_CODE: a.LOCAL_ACCT, ACCT_KEY: a.LOCAL_ACCT_KEY, TP_KEY: a.TP_KEY, AMOUNT: a.CUR_ADJ_AMT, TRANS_DETAILS_KEY: a.TRANS_DETAILS_KEY, IS_DELETE: a.IS_DELETE })),
      ...this.delTransDetails.map(a => ({ ACCOUNT_CODE: a.LOCAL_ACCT, ACCT_KEY: a.LOCAL_ACCT_KEY, TP_KEY: a.TP_KEY, AMOUNT: a.CUR_ADJ_AMT, TRANS_DETAILS_KEY: a.TRANS_DETAILS_KEY, IS_DELETE: a.IS_DELETE }))
    ];

    const message = returnObj.TYPE === 'U' ? 'Reclass has been successfully updated' : 'Reclass has been successfully created';

    this.reclassService.saveReclass(this.GENERAL_CONFIG.base_url, [returnObj], accountArray, this.globalService, this.filterParams)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (response: any) => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          if (response.errorMessage && response.errorMessage !== 'null') {
            this._snackBar.open(response.errorMessage, 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
          } else {
            this._snackBar.open(message, 'Close', { duration: 4000, panelClass: ['success-snackbar'] });
            this.requestGridUpdate.emit('refresh');
          }
          this.cdr.detectChanges();
        },
        () => {
          this.isSaveClicked = false;
          this.crudLoading = false;
          this._snackBar.open('Error occurred while saving. Please try again.', 'Close', { duration: 4000, panelClass: ['error-snackbar'] });
          this.cdr.detectChanges();
        }
      );
  }

  cancel(): void { this.requestGridUpdate.emit('cancel'); }

  trackByIndex(index: number): number { return index; }
}
